import React, { useMemo, useCallback, useEffect } from "react";
import parse, { domToReact, Element, DOMNode } from "html-react-parser";
import DOMPurify from "dompurify";
import clsx from "clsx";

// Default theme constant for easy maintenance
const DEFAULT_THEME: "gray" | "slate" | "zinc" | "neutral" | "stone" = "gray";

// Centralized allowed tags and attributes for reusability
const ALLOWED_TAGS = [
  "h1", "h2", "h3", "h4", "h5", "h6",
  "p", "br", "strong", "em", "u", "s",
  "ul", "ol", "li", "blockquote",
  "a", "img", "span", "div",
  "table", "thead", "tbody", "tr", "th", "td",
  "code", "pre",
];

const ALLOWED_ATTR = [
  "href", "target", "rel", "src", "srcset", "sizes", 
  "alt", "title", "class", "style", "id"
];

export interface HtmlRendererProps {
  html: string;
  className?: string;
  textColor?: string;
  responsive?: boolean;
  proseSize?: "prose-sm" | "prose-base" | "prose-lg" | "prose-xl" | "prose-2xl";
  theme?: "gray" | "slate" | "zinc" | "neutral" | "stone";
  disableProse?: boolean;
  fallback?: React.ReactNode;
  wrapper?: "div" | "span";
  preserveWhiteText?: boolean;
}

const headingClasses = {
  h1: "text-4xl font-bold leading-tight md:text-5xl lg:text-6xl",
  h2: "text-3xl font-semibold md:text-4xl lg:text-5xl",
  h3: "text-2xl font-semibold md:text-3xl lg:text-4xl",
  h4: "text-xl font-medium md:text-2xl lg:text-3xl",
  h5: "text-lg font-medium md:text-xl lg:text-2xl",
  h6: "text-sm font-medium opacity-80 uppercase tracking-wide md:text-base",
};

const HtmlRenderer: React.FC<HtmlRendererProps> = ({
  html,
  className = "",
  textColor,
  responsive = true,
  proseSize,
  theme = DEFAULT_THEME,
  disableProse = false,
  fallback = null,
  wrapper = "div",
  preserveWhiteText = false,
}) => {
  // Configure DOMPurify with secure style handling - only once
  useEffect(() => {
    DOMPurify.addHook("uponSanitizeAttribute", (node, data) => {
      if (data.attrName === "style") {
        // Whitelist only safe CSS properties
        const safeStyle = data.attrValue
          .split(";")
          .filter(rule => {
            const trimmedRule = rule.trim();
            return /^(color|background-color|font-size|font-weight|text-align)\s*:/.test(trimmedRule);
          })
          .join(";");
        data.attrValue = safeStyle;
      }
    });
  }, []);
  const safeHtml = useMemo(() => {
    if (!html) return "";

    const sanitized = DOMPurify.sanitize(html, {
      ALLOWED_TAGS,
      ALLOWED_ATTR,
      FORBID_ATTR: ["onerror", "onload", "onclick"],
    });

    // Improve white text handling with currentColor
    if (!textColor && !preserveWhiteText) {
      const whiteColorPatterns = [
        /color:\s*#fff(fff)?(?![0-9a-fA-F])/gi,
        /color:\s*white\b/gi,
        /color:\s*rgb\(\s*255\s*,\s*255\s*,\s*255\s*\)/gi,
        /color:\s*rgba\(\s*255\s*,\s*255\s*,\s*255\s*,\s*[0-9.]+\s*\)/gi,
        /color:\s*hsl\(\s*0\s*,\s*0%\s*,\s*100%\s*\)/gi,
      ];

      return whiteColorPatterns.reduce(
        (acc, pattern) => acc.replace(pattern, "color: currentColor"),
        sanitized
      );
    }

    return sanitized;
  }, [html, textColor, preserveWhiteText]);

  const proseClasses = useMemo(() => {
    if (disableProse) return "";

    const classes = [`prose`, `prose-${theme}`, "max-w-none"];
    if (proseSize) classes.push(proseSize);
    else if (responsive) classes.push("sm:prose-lg", "lg:prose-xl");

    return classes.join(" ");
  }, [disableProse, proseSize, responsive, theme]);

  // Memoized renderNode with proper typing and enhanced functionality
  const renderNode = useCallback((domNode: DOMNode) => {
    if (!domNode || !(domNode instanceof Element)) return;

    const children = domToReact((domNode as Element).children as any, { replace: renderNode });
    const elementNode = domNode as Element;

    if (headingClasses[elementNode.name as keyof typeof headingClasses]) {
      return React.createElement(elementNode.name, {
        className: clsx(
          headingClasses[elementNode.name as keyof typeof headingClasses],
          elementNode.attribs?.class // preserve original classes
        ),
        style: textColor ? { color: textColor } : undefined,
      }, children);
    }

    switch (elementNode.name) {
      case "p":
        return (
          <p
            className={disableProse ? "text-base leading-relaxed" : "text-base leading-relaxed mb-4"}
            style={textColor ? { color: textColor } : undefined}
          >
            {children}
          </p>
        );
      case "a":
        return (
          <a
            href={elementNode.attribs?.href}
            target="_blank"
            rel="noopener noreferrer"
            className="text-orange-600 underline hover:text-orange-800 transition-colors"
          >
            {children}
          </a>
        );
      case "ul":
        return <ul className="list-disc list-inside mb-4">{children}</ul>;
      case "ol":
        return <ol className="list-decimal list-inside mb-4">{children}</ol>;
      case "li":
        return (
          <li className="text-base leading-relaxed" style={textColor ? { color: textColor } : undefined}>
            {children}
          </li>
        );
      case "blockquote":
        return (
          <blockquote 
            className="border-l-4 border-orange-500 bg-orange-50 py-2 px-4 mb-4 italic"
            role="note"
            aria-label="Quoted text"
          >
            {children}
          </blockquote>
        );
      case "code":
        return (
          <code 
            className="bg-gray-100 px-1 py-0.5 rounded text-sm font-mono"
            role="code"
            aria-label="Code snippet"
          >
            {children}
          </code>
        );
      case "pre":
        return (
          <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto mb-4">
            {children}
          </pre>
        );
      case "img":
        const hasAlt = elementNode.attribs?.alt;
        return (
          <img
            src={elementNode.attribs?.src}
            srcSet={elementNode.attribs?.srcset}
            sizes={elementNode.attribs?.sizes}
            alt={hasAlt || ""}
            title={elementNode.attribs?.title}
            className="rounded-lg max-w-full h-auto my-4"
            loading="lazy"
            role={hasAlt ? "img" : "presentation"}
          />
        );
      default:
        return undefined;
    }
  }, [textColor, disableProse]);

  const parsedContent = useMemo(() => {
    if (!safeHtml.trim()) return null;
    return parse(safeHtml, { replace: renderNode });
  }, [safeHtml, renderNode]);

  if (!parsedContent) {
    return fallback ?? <span className="text-gray-500 italic"></span>;
  }

  return React.createElement(
    wrapper,
    {
      className: [proseClasses, className].filter(Boolean).join(" "),
      role: "article",
      "aria-label": "Sanitized rendered content",
    },
    parsedContent
  );
};

export default HtmlRenderer;
